import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Send } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { FAQS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-blue-100 last:border-b-0"
      data-testid={`faq-item-${index}`}
    >
      <button
        data-testid={`faq-question-${index}`}
        onClick={() => setOpen(!open)}
        className="group flex w-full items-center justify-between gap-4 py-5 text-left"
      >
        <span className="font-display text-sm font-bold text-fv-navy transition-colors duration-300 group-hover:text-fv-blue md:text-base">
          {q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
            open
              ? "border-fv-blue bg-fv-blue text-white"
              : "border-fv-blue/30 text-fv-blue group-hover:border-fv-blue"
          }`}
        >
          <Plus className="h-4 w-4" strokeWidth={2.5} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p
              className="pb-6 pr-12 text-sm leading-relaxed text-fv-navy/60"
              data-testid={`faq-answer-${index}`}
            >
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function GetItSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [showAllFaqs, setShowAllFaqs] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry submitted. Our team will be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const visibleFaqs = showAllFaqs ? FAQS : FAQS.slice(0, 5);

  return (
    <section className="bg-white py-24 lg:py-32" data-testid="get-it-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
        {/* ENQUIRY */}
        <div id="contact" data-testid="enquiry-col" className="scroll-mt-28">
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-[2rem] bg-fv-navy p-8 shadow-2xl shadow-fv-navy/30 md:p-10">
              <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-fv-blue/20 blur-3xl" />
              <div className="flex items-center gap-3">
                <span className="font-display text-sm font-bold tracking-[0.25em] text-fv-cyan">02</span>
                <span className="h-px w-10 bg-fv-cyan/50" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">
                  Enquiry
                </span>
              </div>
              <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-white md:text-3xl">
                HAVE A QUESTION?
              </h2>
              <p className="mt-2 text-sm text-white/60">We're here to help.</p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="enquiry-form">
                {[
                  { key: "name", label: "Name", type: "text", ph: "Your full name" },
                  { key: "email", label: "Email", type: "email", ph: "you@email.com" },
                  { key: "phone", label: "Phone Number", type: "tel", ph: "+60 12-345 6789" },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      {f.label}
                    </label>
                    <input
                      data-testid={`enquiry-${f.key}-input`}
                      type={f.type}
                      required
                      value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.ph}
                      className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder-white/35 outline-none backdrop-blur transition-colors duration-300 focus:border-fv-cyan focus:bg-white/15"
                    />
                  </div>
                ))}
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                    Your Enquiry
                  </label>
                  <textarea
                    data-testid="enquiry-message-input"
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help you?"
                    className="w-full resize-none rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder-white/35 outline-none backdrop-blur transition-colors duration-300 focus:border-fv-cyan focus:bg-white/15"
                  />
                </div>
                <button
                  data-testid="enquiry-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-fv-blue py-4 text-xs font-bold tracking-[0.15em] text-white shadow-lg shadow-fv-blue/40 transition-all duration-300 hover:bg-fv-cyan hover:text-fv-navy disabled:opacity-60"
                >
                  {sending ? "SUBMITTING..." : "SUBMIT ENQUIRY"}
                  <Send className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            </div>
          </Reveal>
        </div>

        {/* FAQ */}
        <div id="faq" data-testid="faq-col" className="scroll-mt-28">
          <Chapter num="03" label="Answers" />
          <Reveal>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-fv-navy md:text-3xl">
              FREQUENTLY ASKED
              <br />
              <span className="text-fv-blue">QUESTIONS</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8" data-testid="faq-list">
              {visibleFaqs.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} index={i} />
              ))}
            </div>
            <button
              data-testid="view-all-faqs-btn"
              onClick={() => setShowAllFaqs(!showAllFaqs)}
              className="mt-7 inline-flex items-center gap-2 rounded-full border-2 border-fv-blue px-6 py-3 text-xs font-bold tracking-[0.12em] text-fv-blue transition-all duration-300 hover:bg-fv-blue hover:text-white"
            >
              {showAllFaqs ? "SHOW FEWER FAQS" : "VIEW ALL FAQS"}
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
